/**
 * Injects the Google Analytics (gtag.js) tag, but only where a property id is
 * configured (VTW-529).
 *
 * The id comes from `runtimeConfig.public.gaId`, set per environment by
 * `NUXT_PUBLIC_GA_ID`. Production sets it; staging and local development leave
 * it unset, so no tag is emitted and no request to googletagmanager.com is made
 * at all — staging traffic can't pollute the production property.
 *
 * This lives in a composable called from `app.vue` rather than in
 * `nuxt.config.ts`'s `app.head`, because `app.head` is evaluated at build time
 * and this project deploys one prebuilt `.output` to every environment: a value
 * baked in there could not differ between staging and production. `app.vue`
 * runs per request on the Nitro server, so the tag is still server-rendered
 * into the HTML — visible in "View Page Source", not injected after hydration.
 *
 * The id is escaped at both interpolation points rather than inlined raw —
 * `encodeURIComponent` in the URL, `JSON.stringify` in the script body. It is
 * operator-supplied, not user input, so this is not an injection defence: it
 * makes a malformed SSM value fail the same predictable way in both places,
 * rather than a stray quote emitting a script with a syntax error that would
 * break every other inline script on the page.
 */
export function useGoogleAnalytics(): void {
  const gaId = useRuntimeConfig().public.gaId;

  if (!gaId) return;

  useHead({
    script: [
      {
        src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`,
        async: true,
      },
      {
        innerHTML: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', ${JSON.stringify(gaId)});`,
      },
    ],
  });
}
